import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageShell from './PageShell';
import SunModeToggle from '../components/SunModeToggle';
import { api } from '../lib/api';
import { BILAN_HABITUDES, RDV_PREALABLE_INFO, getBilanType, getBilanCompetences, getBilanTotal, BILAN_VERDICT_HEADLINES } from '../data/bilanReprise';
import RdvPrealableGuide from '../components/RdvPrealableGuide';
import AccompagnateurQuiz from '../components/AccompagnateurQuiz';
import {
  Calendar, User, AlertCircle, CheckCircle2, XCircle, AlertTriangle, Trophy,
  Loader2, Car, ClipboardCheck, Lightbulb, Printer, Gauge, Clock, Users,
} from 'lucide-react';

const STATE_META = {
  acquis:     { l: 'Acquis',       cls: 'bg-emerald-600 text-white',   Icon: CheckCircle2 },
  consolider: { l: 'À consolider', cls: 'bg-amber-500 text-amber-950', Icon: AlertTriangle },
  non_acquis: { l: 'Non acquis',   cls: 'bg-red-600 text-white',       Icon: XCircle },
};

const VERDICT_META = {
  pret:     { color: 'border-emerald-600/50 bg-emerald-950/25 text-emerald-300', Icon: Trophy },
  presque:  { color: 'border-sky-600/50 bg-sky-950/25 text-sky-300',             Icon: AlertTriangle },
  pas_pret: { color: 'border-amber-600/50 bg-amber-950/25 text-amber-300',       Icon: XCircle },
  pending:  { color: 'border-white/10 bg-white/[0.03] text-white/60',            Icon: ClipboardCheck },
};

const CONSEILS_PARENTS_50 = [
  'Laissez-le conduire à chaque occasion (courses, trajets réguliers) : la régularité ancre les acquis.',
  'Variez progressivement les conditions : nuit, pluie, voie rapide, centre-ville chargé.',
  'Laissez-le décider seul : ne guidez pas chaque manœuvre, intervenez uniquement pour la sécurité.',
  'Débriefez APRÈS le trajet, au calme — jamais pendant la conduite.',
  'Reprenez ensemble les points « à consolider » du bilan, sans pression ni stress.',
];

function ConseilsParents({ pct }) {
  if (!(pct > 50)) return null;
  return (
    <div className="mt-6 rounded-2xl border border-emerald-600/40 bg-gradient-to-br from-emerald-950/25 to-[#0d0d0e] p-5" data-testid="bilan-conseils-parents-50">
      <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-1.5 inline-flex items-center gap-1.5">
        <Trophy size={12} /> Bonne nouvelle · plus de 50 % d&apos;acquis
      </div>
      <p className="text-white text-[14px] font-semibold leading-snug">L&apos;élève est sur la bonne voie ! Voici comment l&apos;aider à progresser encore, accompagnateurs :</p>
      <ul className="mt-3 space-y-2">
        {CONSEILS_PARENTS_50.map((c, i) => (
          <li key={i} className="flex gap-2.5 text-emerald-50/90 text-[13px] leading-relaxed">
            <CheckCircle2 size={14} className="shrink-0 mt-0.5 text-emerald-400" />
            <span>{c}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BilanReprisePublicPage() {
  const { token } = useParams();
  const [bilan, setBilan] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const b = await api.publicBilanReprise(token);
        if (alive) setBilan(b);
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || 'Bilan introuvable ou expiré.');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // Bilan EN DIRECT — polling toutes les 2,5 s tant que l'évaluation est en cours
  const isLive = bilan?.status === 'live';
  useEffect(() => {
    if (!isLive) return undefined;
    const iv = setInterval(async () => {
      try {
        const b = await api.publicBilanReprise(token);
        setBilan(b);
      } catch { /* on garde le dernier état */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [isLive, token]);

  if (error) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto p-12 text-center">
          <AlertCircle className="mx-auto text-red-400 mb-4" size={32} />
          <h2 className="font-serif-editorial text-white text-[26px]">Bilan introuvable</h2>
          <p className="text-white/60 mt-2 text-[14px]">{error}</p>
          <Link to="/" className="inline-block mt-6 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold">Retour à l&apos;accueil</Link>
        </div>
      </PageShell>
    );
  }
  if (!bilan) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto p-12 text-center text-white/50">
          <Loader2 className="animate-spin mx-auto mb-3" size={24} /> Chargement de ton bilan…
        </div>
      </PageShell>
    );
  }

  const dateFr = bilan.date ? new Date(bilan.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const typeId = bilan.bilan_type || (bilan.parcours_type === 'supervisee' ? 'retour_supervisee' : 'retour_aac');
  const type = getBilanType(typeId);

  // ═══ VUE EN DIRECT — progression globale pendant que l'enseignant évalue ═══
  if (isLive) {
    const lStats = bilan.stats || {};
    const lTotal = lStats.total || getBilanTotal(typeId);
    const lDone = (lStats.acquis || 0) + (lStats.consolider || 0) + (lStats.non_acquis || 0);
    const lProgress = Math.min(100, Math.round((lDone / lTotal) * 100));
    return (
      <PageShell>
        <SunModeToggle />
        <section className="max-w-3xl mx-auto px-4 lg:px-6 py-8 sm:py-12" data-testid="bilan-public-live">
          {/* Hero live */}
          <div className="rounded-2xl border border-red-500/30 bg-gradient-to-br from-[#150909] via-[#0d0d0e] to-[#0d0d0e] p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-8 -right-8 opacity-[0.06] pointer-events-none"><Car size={220} strokeWidth={1} /></div>
            <div className="relative">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600 text-white text-[10.5px] uppercase tracking-[0.2em] font-bold font-mono-spec" data-testid="bilan-live-badge">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Évaluation en cours
              </span>
              <h1 className="mt-3 font-serif-editorial text-white text-[28px] sm:text-[34px] leading-tight">{bilan.student_name} est au volant</h1>
              <p className="mt-2 text-white/65 text-[14px]">
                <span className="text-white font-semibold">{bilan.teacher_name}</span> évalue {bilan.student_name} en ce moment — {type.short.toLowerCase()}.
                Cette page se met à jour <strong className="text-white">automatiquement</strong>.
              </p>
              {dateFr && <div className="mt-3 text-[12.5px] text-white/60 inline-flex items-center gap-1.5"><Calendar size={12} className="text-white/40" /> {dateFr}</div>}
            </div>
          </div>

          {/* Progression globale */}
          <div className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d0e] p-6" data-testid="bilan-live-progress">
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-300 font-bold font-mono-spec">Progression de l&apos;évaluation</div>
                <div className="font-serif-editorial text-white text-[34px] leading-none mt-2">{lDone}<span className="text-white/35 text-[20px]"> / {lTotal} critères</span></div>
              </div>
              <div className="font-mono-spec text-white/50 text-[13px]">{lProgress}%</div>
            </div>
            <div className="h-3 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-red-600 via-amber-500 to-emerald-500 transition-all duration-700" style={{ width: `${lProgress}%` }} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/15 p-3.5 text-center">
                <div className="text-[10.5px] uppercase tracking-wider text-emerald-300 font-bold font-mono-spec">Acquis</div>
                <div className="font-serif-editorial text-emerald-400 text-[28px] leading-none mt-1" data-testid="bilan-live-acquis">{lStats.acquis || 0}</div>
              </div>
              <div className="rounded-xl border border-amber-700/30 bg-amber-950/15 p-3.5 text-center">
                <div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">À consolider</div>
                <div className="font-serif-editorial text-amber-400 text-[28px] leading-none mt-1">{lStats.consolider || 0}</div>
              </div>
              <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-3.5 text-center">
                <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Non acquis</div>
                <div className="font-serif-editorial text-red-400 text-[28px] leading-none mt-1">{lStats.non_acquis || 0}</div>
              </div>
            </div>
            <p className="mt-4 text-white/50 text-[12.5px] leading-relaxed text-center">
              Le <strong className="text-white/80">détail complet des critères et le verdict</strong> seront révélés ici même dès que l&apos;enseignant finalisera le bilan en fin de leçon.
            </p>
          </div>

          {/* Conseils aux accompagnateurs — dès 50 % d'acquis */}
          <ConseilsParents pct={bilan.score_pct} />

          {/* Pendant ce temps : guide + quiz pour les accompagnateurs */}
          {typeId === 'rdv_prealable' && (
            <div className="mt-8" data-testid="bilan-live-guide">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-mono-spec font-bold mb-3">
                En attendant · guide de l&apos;accompagnateur
              </div>
              <RdvPrealableGuide />
              <div className="mt-3">
                <AccompagnateurQuiz />
              </div>
            </div>
          )}

          <p className="mt-8 text-center text-white/35 text-[11px] font-mono-spec">
            Bilan en direct · {bilan.teacher_name} · codevoiture.fr
          </p>
        </section>
      </PageShell>
    );
  }

  const isRetour = type.family === 'retour';
  const comps = getBilanCompetences(typeId);
  const stats = bilan.stats || { acquis: 0, consolider: 0, non_acquis: 0, habitudes: 0 };
  const total = stats.total || getBilanTotal(typeId);
  const pct = bilan.score_pct != null ? bilan.score_pct : Math.round((stats.acquis / total) * 100);
  const vMeta = VERDICT_META[bilan.verdict_kind] || VERDICT_META.pending;
  const headline = (BILAN_VERDICT_HEADLINES[type.family] || {})[bilan.verdict_kind] || 'En cours d\'évaluation';
  const VIcon = vMeta.Icon;
  const grid = bilan.grid || {};
  const habitudes = bilan.habitudes || {};
  const habChecked = isRetour ? BILAN_HABITUDES.filter((h) => habitudes[h.id]) : [];

  return (
    <PageShell>
      <SunModeToggle />
      <section className="max-w-3xl mx-auto px-4 lg:px-6 py-8 sm:py-12" data-testid="bilan-public">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6 no-print">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Bilan de reprise</span>
        </nav>

        {/* Hero */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#091509] via-[#0d0d0e] to-[#0d0d0e] p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 opacity-[0.06] pointer-events-none">
            <Car size={220} strokeWidth={1} />
          </div>
          <div className="relative">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-mono-spec font-bold">{type.short}</div>
            <h1 className="mt-2 font-serif-editorial text-white text-[28px] sm:text-[34px] leading-tight">Bonjour {bilan.student_name}</h1>
            <p className="mt-2 text-white/65 text-[14px]">Voici ton bilan réalisé avec <span className="text-white font-semibold">{bilan.teacher_name}</span>.{isRetour ? ' À partager avec tes accompagnateurs !' : ''}</p>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[12.5px] text-white/70">
              {bilan.date && <span className="inline-flex items-center gap-1.5"><Calendar size={12} className="text-white/40" /> {dateFr}</span>}
              {bilan.km_parcourus != null && <span className="inline-flex items-center gap-1.5"><Gauge size={12} className="text-white/40" /> {bilan.km_parcourus.toLocaleString('fr-FR')} km parcourus</span>}
              {bilan.duree_mois != null && <span className="inline-flex items-center gap-1.5"><Clock size={12} className="text-white/40" /> {bilan.duree_mois} mois</span>}
              {bilan.nb_rdv_peda != null && <span className="inline-flex items-center gap-1.5"><Users size={12} className="text-white/40" /> {bilan.nb_rdv_peda} RDV pédagogique{bilan.nb_rdv_peda > 1 ? 's' : ''}</span>}
            </div>
          </div>
        </div>

        {/* Verdict */}
        <div className={`mt-6 rounded-2xl border-2 p-6 ${vMeta.color}`} data-testid="bilan-public-verdict">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
              <VIcon size={26} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold opacity-90">{type.family === 'retour' ? 'Verdict permis blanc' : type.family === 'rdv' ? 'Verdict AFFI · conduite accompagnée' : 'Verdict de la compétence'}</div>
              <div className="mt-1 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">{headline}</div>
              <p className="mt-2 text-white/80 text-[13.5px] leading-relaxed">{bilan.verdict}</p>
            </div>
          </div>
        </div>

        {/* Conseils aux accompagnateurs — dès 50 % d'acquis */}
        <ConseilsParents pct={pct} />

        {/* Stats */}
        <div className={`mt-4 grid grid-cols-2 ${isRetour ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-3`}>
          <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-3.5 text-center col-span-2 sm:col-span-1">
            <div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Score</div>
            <div className="font-serif-editorial text-white text-[26px] leading-none mt-1">{pct}<span className="text-white/40 text-[15px]">%</span></div>
          </div>
          <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-emerald-300 font-bold font-mono-spec">Acquis</div>
            <div className="font-serif-editorial text-emerald-400 text-[26px] leading-none mt-1">{stats.acquis}</div>
          </div>
          <div className="rounded-xl border border-amber-700/30 bg-amber-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">À consolider</div>
            <div className="font-serif-editorial text-amber-400 text-[26px] leading-none mt-1">{stats.consolider}</div>
          </div>
          <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Non acquis</div>
            <div className="font-serif-editorial text-red-400 text-[26px] leading-none mt-1">{stats.non_acquis}</div>
          </div>
          {isRetour && (
            <div className="rounded-xl border-2 border-amber-700/50 bg-amber-950/25 p-3.5 text-center">
              <div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">Habitudes</div>
              <div className="font-serif-editorial text-amber-400 text-[26px] leading-none mt-1">{habChecked.length}</div>
            </div>
          )}
        </div>

        {/* Habitudes à corriger — message clé pour les accompagnateurs */}
        {habChecked.length > 0 && (
          <div className="mt-6 rounded-2xl border border-amber-600/40 bg-amber-950/15 p-5" data-testid="bilan-public-habitudes">
            <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-1.5 inline-flex items-center gap-1.5">
              <AlertTriangle size={12} /> Habitudes à corriger — message aussi pour les accompagnateurs
            </div>
            <p className="text-white/55 text-[12.5px] mb-3">Ces automatismes ont été observés pendant l&apos;évaluation. Ils se corrigent vite… si tout le monde y veille pendant les prochains trajets.</p>
            <ul className="space-y-2">
              {habChecked.map((h) => (
                <li key={h.id} className="flex gap-2.5 text-amber-100 text-[13.5px] leading-snug">
                  <span className="shrink-0 mt-0.5"><AlertTriangle size={13} className="text-amber-400" /></span>
                  <span>{h.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Conseils */}
        {bilan.conseils && (
          <div className="mt-6 rounded-xl border border-sky-500/30 bg-sky-950/15 p-5" data-testid="bilan-public-conseils">
            <div className="text-sky-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-2 inline-flex items-center gap-1.5"><Lightbulb size={12} /> Conseils de ton enseignant</div>
            <p className="text-white/85 text-[14px] leading-relaxed whitespace-pre-wrap">{bilan.conseils}</p>
          </div>
        )}

        {/* Guide conduite accompagnée — pour l'élève ET les accompagnateurs (RDV Préalable) */}
        {type.id === 'rdv_prealable' && (
          <div className="mt-8" data-testid="bilan-public-rdv-guide">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-mono-spec font-bold mb-3">
              Guide conduite accompagnée · à lire avec tes accompagnateurs
            </div>
            <div className="rounded-2xl border border-amber-600/35 bg-amber-950/15 p-4 mb-3" data-testid="bilan-public-rdv-apres">
              <div className="text-amber-300 text-[11px] font-bold font-mono-spec uppercase tracking-wider mb-2">Après le rendez-vous — à ne pas oublier</div>
              <ul className="space-y-1.5">
                {RDV_PREALABLE_INFO.apres.map((a, i) => (
                  <li key={i} className="flex gap-2.5 text-amber-100/90 text-[12.5px] leading-snug">
                    <span className="shrink-0 mt-0.5 text-amber-400 font-bold">→</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <RdvPrealableGuide />
            <div className="mt-3 no-print">
              <AccompagnateurQuiz />
            </div>
          </div>
        )}

        {/* Détail par compétence */}
        <div className="mt-8 space-y-4" data-testid="bilan-public-detail">
          {comps.map((comp) => (
            <div key={comp.id} className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
              <div className="px-4 py-3 bg-emerald-600/10 border-b border-emerald-700/30">
                <div className="text-[11.5px] text-emerald-300 font-bold font-mono-spec uppercase tracking-wider">{comp.id} · {comp.titre}</div>
              </div>
              <ul className="divide-y divide-white/5">
                {comp.criteres.map((r) => {
                  const meta = grid[r.id] ? STATE_META[grid[r.id]] : null;
                  return (
                    <li key={r.id} className="grid grid-cols-[1fr_auto] gap-2 px-4 py-2.5 items-center">
                      <div className="text-white/85 text-[12.5px] leading-snug">{r.label}</div>
                      {meta ? (
                        <span className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-bold ${meta.cls}`}>
                          <meta.Icon size={9} /> {meta.l}
                        </span>
                      ) : (
                        <span className="text-white/30 text-[10px] font-mono-spec">—</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-center no-print">
          <button onClick={() => window.print()} className="h-10 px-4 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/35 text-[12.5px] font-semibold inline-flex items-center gap-1.5">
            <Printer size={12} /> Imprimer / PDF
          </button>
        </div>

        <p className="mt-6 text-center text-white/35 text-[11px] font-mono-spec">
          {type.short} généré par {bilan.teacher_name} · codevoiture.fr · Consulté {bilan.view_count} fois
        </p>
      </section>
    </PageShell>
  );
}
